import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useContext, useEffect, useMemo } from 'react';

import { SocketContext } from '../providers/SocketProvider/SocketProvider';
import backendService from '../services/backendService';

const usePoll = ({
  gameId,
  roundId,
  isAuthenticated,
  tenantId,
}: {
  gameId?: string;
  roundId?: string;
  isAuthenticated: boolean;
  // Pass tenantId during a live game to subscribe to the shared
  // `onRoundResponsesChanged` socket event and refetch the aggregated
  // counts whenever a student responds. Without it, usePoll falls back
  // to a one-shot fetch — fine for static previews / thumbnails.
  tenantId?: string;
}) => {
  const queryClient = useQueryClient();
  const { gameAPI } = useContext(SocketContext);

  const queryKey = useMemo(() => ['poll', roundId, gameId], [roundId, gameId]);

  const { data, isFetching } = useQuery({
    queryKey,

    queryFn: async _ => {
      if (!gameId || !roundId) return Promise.resolve(undefined);
      if (isAuthenticated)
        return backendService.instance.game.retrievePoll({ roundId, gameId });
      return backendService.instance.game.retrievePublicPoll({ roundId, gameId });
    },

    enabled: Boolean(gameId && roundId),
  });

  // Live invalidation: whenever the server pushes a `responses:changed`
  // event for this round, refetch the aggregated poll data. Closes the
  // race where a student response lands between mount and the first
  // retrievePoll resolving. Same pipe used by `useResponses` — see
  // hooks/useResponses.tsx and gameAPI.ts:708.
  useEffect(() => {
    if (!gameId || !roundId || !tenantId || !gameAPI || !isAuthenticated) return;
    const sub = gameAPI.game
      .onRoundResponsesChanged({ tenantId, gameId, slideId: roundId })
      .subscribe(() => {
        queryClient.invalidateQueries({ queryKey: ['poll', roundId, gameId] });
      });
    return () => sub.unsubscribe();
  }, [gameId, roundId, tenantId, gameAPI, queryClient, isAuthenticated]);

  return {
    data,

    isLoading: isFetching,
  };
};
export default usePoll;
